import { Button, Chip, IconButton, Stack, Box } from "@mui/material";
import React, { useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
  CustomTable,
  DeleteModal,
  CustomModal,
  Loader,
  usePermission,
} from "../../../shared";
import { createColumnHelper } from "@tanstack/react-table";
import DeleteIcon from "../../../assets/images/Icons/TrashIcon.png";
import { AddStockItemForm } from "./AddStockItemForm";
import { useRecipeStockItems } from "../hooks";
import { formatCost, formatWeightWithUnits } from "../../../utils";

export const RecipeStockItems = ({
  id,
  ingredientGroups = [],
  globalData,
  alternativeunitList = [],
}) => {
  const {
    recipeStockItemsQuery,
    deleteInfo,
    handleDelete,
    confirmDelete,
    resetDeleteInfo,
    openAddModal,
    isEdit,
    handleAddItem,
    handleEditItem,
    handleCloseModal,
    handleSaveItem,
    formData,
    handleFormChange,
    handleAddGroups,
    handleRemoveGroup,
    validator,
    isCreating,
    isUpdating,
    isLoadingDetails,
    alternativeunitList: altUnitList,
    hasFormChanged,
  } = useRecipeStockItems({ recipeId: id, alternativeunitList });
  const canStockTakeDelete = usePermission("recipe-stock-take-delete");
  const canStockTakeCreate = usePermission("recipe-stock-take-create");
  const canStockTakeModify = usePermission("recipe-stock-take-modify");

  const modalTitle = isEdit
    ? canStockTakeModify
      ? "Edit Stock Item"
      : "Stock Item"
    : "Add Stock Item";

  // Get stock items data from API
  const stockItems = recipeStockItemsQuery?.data || [];
  const columnHelper = createColumnHelper();
  const columns = useMemo(
    () => [
      columnHelper.accessor("stock_item_name", {
        header: () => "Stock Item Name",
        sortDescFirst: true,
      }),

      columnHelper.accessor("stock_code", {
        header: () => "Stock Code",
        cell: (info) => info.getValue() || "--",
        sortDescFirst: true,
      }),

      columnHelper.accessor("status", {
        header: "Stocktake Status",
        cell: ({ row }) => {
          return row?.original.status ? (
            <Chip size="small" variant="generated" label="ENABLED" />
          ) : (
            <Chip size="small" variant="stockTakeDraft" label="DISABLED" />
          );
        },
      }),

      columnHelper.accessor("total_weight", {
        header: "Stock Unit",
        cell: ({ row }) => {
          const isAltUom = row?.original?.is_alternate_UOM;
          const altUomUnit = row?.original?.alt_units?.[0];

          const totalWeightUnit =
            isAltUom && altUomUnit
              ? altUomUnit.alt_unit
              : row?.original?.total_weight_unit;

          return totalWeightUnit;
        },
      }),
      // columnHelper.accessor("total_weight", {
      //   header: "Weight",
      //   cell: ({ row }) => {
      //     const isAltUom = row?.original?.is_alternate_UOM;
      //     const altUomUnit = row?.original?.alt_units?.[0];

      //     const totalWeight =
      //       isAltUom && altUomUnit
      //         ? altUomUnit?.alt_unit_qty
      //         : row?.original?.total_weight;

      //     const totalWeightUnit =
      //       isAltUom && altUomUnit
      //         ? altUomUnit.alt_unit
      //         : row?.original?.total_weight_unit;

      //     return formatWeightWithUnits(
      //       totalWeight,
      //       totalWeightUnit,
      //       globalData?.all_Units,
      //     );
      //   },
      // }),

      columnHelper.accessor("total_cost_exc_gst", {
        header: "Cost (Ex GST)",
        cell: ({ row }) =>
          `$${formatCost(row?.original?.total_cost_exc_gst || 0)}`,
      }),

      columnHelper.display({
        id: "actions",
        cell: (props) => {
          const { row } = props;
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap="10px"
            >
              {canStockTakeDelete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDelete(row.original.id);
                  }}
                  sx={{ p: 0 }}
                >
                  <img src={DeleteIcon} alt="delete icon" />
                </IconButton>
              )}
              <img alt="details" src="/icons/arrow_forward.png" />
            </Stack>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleDelete],
  );

  return (
    <>
      <CustomTable
        defaultData={stockItems ?? []}
        columns={columns}
        headerText="Stock Items"
        emptyTableMessage="No stock items added yet!"
        isLoading={recipeStockItemsQuery?.isLoading}
        onRowAction={(row) => {
          return handleEditItem(row.id);
        }}
        rightContent={
          canStockTakeCreate && (
            <Stack direction="row" gap="8px">
              <Button
                sx={{
                  height: "36px",
                  border: "1px solid #BCC7DB",
                  borderRadius: "10px",
                  borderColor: "#BCC7DB",
                  color: "#111C2B",
                }}
                type="button"
                size="medium"
                variant="outlined"
                onClick={handleAddItem}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Stack>
          )
        }
        tableHeaderStyles={{
          px: 0,
          fontSize: "18px",
          fontWeight: 500,
          height: "56px",
          py: "12px",
          borderBottom: "1px solid #E0E0E0",
        }}
        headerContentStyle={{ px: 0 }}
        enableTableContainerPaddingBottom
      />

      {canStockTakeDelete && (
        <DeleteModal
          open={deleteInfo.show}
          handleClose={resetDeleteInfo}
          onConfirm={confirmDelete}
        />
      )}

      <CustomModal
        open={openAddModal}
        handleClose={handleCloseModal}
        title={modalTitle}
        onSubmit={handleSaveItem}
        isEdit={isEdit}
        isDisableSubmit={
          isCreating ||
          isUpdating ||
          isLoadingDetails ||
          !formData?.groups?.length ||
          !canStockTakeModify ||
          !hasFormChanged
        }
        PaperProps={{
          sx: {
            minWidth: { xs: "90%", md: "770px", lg: "968px" },
            minHeight: "556px",
          },
        }}
        content={
          isLoadingDetails ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
              }}
            >
              <Loader />
            </Box>
          ) : (
            <AddStockItemForm
              ingredientGroups={ingredientGroups}
              globalData={globalData}
              alternativeunitList={altUnitList}
              formData={formData}
              handleFormChange={handleFormChange}
              handleAddGroups={handleAddGroups}
              handleRemoveGroup={handleRemoveGroup}
              validator={validator}
            />
          )
        }
      />
    </>
  );
};
